import { LucideIcon, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SystemHealthCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon?: LucideIcon;
  percentage?: number;
  thresholds?: {
    warning: number;
    critical: number;
  };
  onRefresh?: () => void;
  refreshing?: boolean;
  className?: string;
}

const SystemHealthCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  percentage,
  thresholds = { warning: 70, critical: 85 },
  onRefresh,
  refreshing,
  className
}: SystemHealthCardProps) => {
  const getStatusColor = () => {
    if (!percentage) return 'border-border';
    if (percentage >= thresholds.critical) return 'border-red-500/30 bg-red-500/5';
    if (percentage >= thresholds.warning) return 'border-yellow-500/30 bg-yellow-500/5';
    return 'border-green-500/30 bg-green-500/5';
  };

  const getValueColor = () => {
    if (!percentage) return 'text-foreground';
    if (percentage >= thresholds.critical) return 'text-red-600 dark:text-red-400';
    if (percentage >= thresholds.warning) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  return (
    <Card className={cn('hover:shadow-md transition-all flex flex-col', getStatusColor(), className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground truncate whitespace-nowrap">
          {title}
        </CardTitle>
        <div className="flex items-center gap-2 shrink-0">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          {onRefresh && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onRefresh}
              disabled={refreshing}
              className="h-6 w-6"
            >
              <RefreshCw className={cn("h-3 w-3", refreshing && "animate-spin")} />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col justify-between flex-1">
        <div className={cn("text-2xl font-bold leading-tight", getValueColor())}>
          {value}
        </div>
        <div className="mt-2 min-h-[18px] flex flex-col justify-end">
          {subtitle && (
            <p className="text-xs text-muted-foreground mb-1 truncate">
              {subtitle}
            </p>
          )}
          {percentage !== undefined ? (
            <div className="w-full bg-secondary rounded-full h-1.5">
              <div
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  percentage >= thresholds.critical ? "bg-red-500" :
                  percentage >= thresholds.warning ? "bg-yellow-500" :
                  "bg-green-500"
                )}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              />
            </div>
          ) : (
            !subtitle && <div className="h-1.5" />
          )}
        </div>
      </CardContent>
    </Card>
  );
};


export default SystemHealthCard;
